import { camelCase, snakeCase } from './string';

type SnakeToCamel<S extends string> = S extends `${infer Head}_${infer Tail}` ? `${Head}${Capitalize<SnakeToCamel<Tail>>}` : S;

export type CamelCaseKeys<T> = T extends readonly unknown[]
  ? { [K in keyof T]: CamelCaseKeys<T[K]> }
  : T extends object
    ? {
        [K in keyof T as SnakeToCamel<string & K>]: CamelCaseKeys<T[K]>;
      }
    : T;

type CamelToSnake<S extends string> = S extends `${infer Head}${infer Tail}` ? `${Lowercase<Head>}${CamelToSnakeHelper<Tail>}` : S;

type CamelToSnakeHelper<S extends string> = S extends `${infer First}${infer Rest}` ? (First extends Uppercase<First> ? `_${Lowercase<First>}${CamelToSnakeHelper<Rest>}` : `${First}${CamelToSnakeHelper<Rest>}`) : '';

export type SnakeCaseKeys<T> = T extends readonly unknown[]
  ? { [K in keyof T]: SnakeCaseKeys<T[K]> }
  : T extends object
    ? {
        [K in keyof T as CamelToSnake<string & K>]: SnakeCaseKeys<T[K]>;
      }
    : T;

const isPrimitive = (val: unknown) => ['string', 'number', 'boolean'].includes(typeof val);

const localize = <T>(obj: T, toStyle: (str: string, isUpper?: boolean) => string): T | CamelCaseKeys<T> | SnakeCaseKeys<T> => {
  // プリミティブ型なら変換しない
  if (isPrimitive(obj) || obj === null) {
    return obj;
  }

  // 配列の中身がオブジェクトの場合のみキーを変換する
  if (Array.isArray(obj)) {
    return obj.map((a) => localize(a, toStyle)) as CamelCaseKeys<T> | SnakeCaseKeys<T>;
  }

  // オブジェクトのキーを変換する
  if (typeof obj === 'object') {
    const dest: Record<string, unknown> = {};
    Object.keys(obj).forEach((key) => {
      dest[toStyle(key)] = localize((obj as Record<string, unknown>)[key], toStyle);
    });
    return dest as CamelCaseKeys<T> | SnakeCaseKeys<T>;
  }

  return obj;
};

export const toCamelCaseKeys = <T>(obj: T) => localize(obj, camelCase) as CamelCaseKeys<T>;

export const toSnakeCaseKeys = <T>(obj: T) => localize(obj, snakeCase) as SnakeCaseKeys<T>;
